import React, { useState, useEffect, createRef, useRef, useCallback } from 'react';
import { CircularProgress, Grid, Box, Typography } from '@mui/material';
import PlaceDetails from '../PlaceDetails/PlaceDetails';
import { styled } from '@mui/system';
import { useSmoothScroll } from '../../hooks/useSmoothScroll';
import VirtualizedGrid from './VirtualizedGrid';
import { debounce } from '../../utils/scrollPhysics';

// This container will fill the space given by parent layouts
const StyledContainer = styled(Box)({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: '8px 5px 15px 15px',
  boxSizing: 'border-box',
  position: 'relative',
  overflow: 'hidden', // Hide overflow since we handle scrolling manually
});

// Make loader fill the container
const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
});

// Smooth scrolling container
const SmoothScrollContainer = styled(Box)({
  flexGrow: 1,
  position: 'relative',
  overflow: 'hidden',
  // Hardware acceleration
  willChange: 'transform',
  transform: 'translateZ(0)',
});

// Content wrapper that moves with smooth scrolling
const ScrollableContent = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  willChange: 'transform',
  transform: 'translateZ(0)',
});

// Language-specific empty messages
const emptyMessages = {
  TH: 'ไม่พบกิจกรรม',
  EN: 'No events found matching your criteria.',
  CN: '未找到符合条件的活动'
};

const SmoothScrollList = ({
  places,
  childClicked,
  isLoading,
  onLocationClick,
  language = 'TH',
  onActiveIndexChange,
  enableVirtualization = true
}) => {
  const [elRefs, setElRefs] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  const scrollContainerRef = useRef(null);
  const contentRef = useRef(null);
  const isScrollingFromClickRef = useRef(false);
  const resizeObserverRef = useRef(null);

  // Calculate scroll bounds based on content and container heights
  const scrollBounds = {
    min: Math.min(0, containerHeight - contentHeight),
    max: 0
  };

  /**
   * Handle scroll position updates from smooth scroll hook
   */
  const handleScroll = useCallback((position, scrollInfo) => {
    setScrollPosition(position);

    // Apply transform to content for smooth movement
    if (contentRef.current) {
      contentRef.current.style.transform = `translate3d(0, ${position}px, 0)`;
    }

    // Skip centered card detection during momentum or dragging
    if (scrollInfo.isMomentum || scrollInfo.isDragging || isScrollingFromClickRef.current) {
      return;
    }

    // Debounced centered card detection
    debouncedFindCenteredCard();
  }, []);

  /**
   * Handle momentum scroll start
   */
  const handleMomentumStart = useCallback(() => {
    // Optional: Add visual feedback for momentum scrolling
  }, []);

  /**
   * Handle momentum scroll end
   */
  const handleMomentumEnd = useCallback(() => {
    // Trigger centered card detection after momentum ends
    setTimeout(() => {
      findCenteredCard();
    }, 100);
  }, []);

  // Initialize smooth scroll hook
  const {
    isScrolling,
    isMomentumScrolling,
    setScrollPosition: setSmoothScrollPosition,
    getScrollPosition,
    ...scrollHandlers
  } = useSmoothScroll({
    direction: 'vertical',
    onScroll: handleScroll,
    onMomentumStart: handleMomentumStart,
    onMomentumEnd: handleMomentumEnd,
    bounds: scrollBounds
  });

  /**
   * Find the centered card in the viewport
   */
  const findCenteredCard = useCallback(() => {
    if (!scrollContainerRef.current || !elRefs.length || !Array.isArray(places)) return;

    // Skip if we're in the middle of scrolling from a map click
    if (isScrollingFromClickRef.current) return;

    const container = scrollContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    const containerCenter = containerRect.top + containerRect.height / 2;

    let closestIndex = 0;
    let minDistance = Infinity;

    elRefs.forEach((ref, index) => {
      if (ref?.current) {
        const elementRect = ref.current.getBoundingClientRect();
        const elementCenter = elementRect.top + elementRect.height / 2;
        const distance = Math.abs(elementCenter - containerCenter);

        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      }
    });

    if (closestIndex !== activeIndex && onActiveIndexChange) {
      setActiveIndex(closestIndex);
      onActiveIndexChange(closestIndex);
    }
  }, [elRefs, places, activeIndex, onActiveIndexChange]);

  // Debounced version for performance
  const debouncedFindCenteredCard = useCallback(
    debounce(findCenteredCard, 150),
    [findCenteredCard]
  );

  /**
   * Scroll to specific item programmatically
   */
  const scrollToItem = useCallback((index) => {
    if (!elRefs[index]?.current || !scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const element = elRefs[index].current;

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    // Calculate position to center the element
    const containerCenter = containerRect.height / 2;
    const elementHeight = elementRect.height;
    const elementTop = elementRect.top - containerRect.top;

    const targetPosition = getScrollPosition() - (elementTop - containerCenter + elementHeight / 2);

    // Set flag to indicate this scroll is from a map click
    isScrollingFromClickRef.current = true;

    setSmoothScrollPosition(targetPosition, true);

    // Reset flag after scroll animation
    setTimeout(() => {
      isScrollingFromClickRef.current = false;
      findCenteredCard();
    }, 1000);
  }, [elRefs, setSmoothScrollPosition, getScrollPosition, findCenteredCard]);

  /**
   * Update content and container heights
   */
  const updateHeights = useCallback(() => {
    if (scrollContainerRef.current && contentRef.current) {
      const containerRect = scrollContainerRef.current.getBoundingClientRect();
      const contentRect = contentRef.current.getBoundingClientRect();

      setContainerHeight(containerRect.height);
      setContentHeight(contentRect.height);
    }
  }, []);

  // Effect to create refs for scrolling
  useEffect(() => {
    const placesCount = Array.isArray(places) ? places.length : 0;
    setElRefs((refs) =>
      Array(placesCount)
        .fill()
        .map((_, i) => refs[i] || createRef())
    );
  }, [places]);

  // Effect to set up ResizeObserver for dynamic height updates
  useEffect(() => {
    if (!scrollContainerRef.current || !contentRef.current) return;

    const container = scrollContainerRef.current;
    const content = contentRef.current;

    resizeObserverRef.current = new ResizeObserver(() => {
      updateHeights();
    });

    resizeObserverRef.current.observe(container);
    resizeObserverRef.current.observe(content);

    // Initial height calculation
    updateHeights();

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [updateHeights]);

  // Effect to handle external scroll requests (from map clicks)
  useEffect(() => {
    if (childClicked !== null && elRefs[childClicked]?.current) {
      scrollToItem(childClicked);
    }
  }, [childClicked, scrollToItem]);

  // Effect for initial centered card detection
  useEffect(() => {
    if (onActiveIndexChange && elRefs.length > 0) {
      setTimeout(() => {
        findCenteredCard();
      }, 100);
    }
  }, [findCenteredCard, onActiveIndexChange, elRefs.length]);

  /**
   * Get appropriate empty message based on language
   */
  const getEmptyMessage = () => {
    return emptyMessages[language] || emptyMessages.EN;
  };

  // Check if places is valid before mapping
  const validPlaces = Array.isArray(places) ? places : [];

  // Use virtualization for large lists
  const shouldUseVirtualization = enableVirtualization && validPlaces.length > 100;

  return (
    <StyledContainer>
      {isLoading ? (
        <LoadingContainer>
          <CircularProgress size={50} />
        </LoadingContainer>
      ) : (
        <SmoothScrollContainer
          ref={scrollContainerRef}
          {...scrollHandlers}
          sx={{
            cursor: isScrolling ? 'grabbing' : 'grab',
            userSelect: 'none', // Prevent text selection during drag
          }}
        >
          <ScrollableContent ref={contentRef}>
            {validPlaces.length > 0 ? (
              shouldUseVirtualization ? (
                <VirtualizedGrid
                  places={validPlaces}
                  elRefs={elRefs}
                  childClicked={childClicked}
                  onLocationClick={onLocationClick}
                  language={language}
                  scrollPosition={scrollPosition}
                  containerHeight={containerHeight}
                />
              ) : (
                <Grid container spacing={0}>
                  {validPlaces.map((place, i) => (
                    <Grid
                      ref={elRefs[i]}
                      key={place.event_id || place.id || `place-${i}`}
                      item
                      xs={12}
                    >
                      <PlaceDetails
                        place={place}
                        selected={Number(childClicked) === i}
                        refProp={elRefs[i]}
                        onLocationClick={onLocationClick}
                        language={language}
                      />
                    </Grid>
                  ))}
                </Grid>
              )
            ) : (
              <Box sx={{ width: '100%', textAlign: 'center', padding: 2 }}>
                <Typography>{getEmptyMessage()}</Typography>
              </Box>
            )}
          </ScrollableContent>

          {/* Scroll indicator (optional) */}
          {isMomentumScrolling && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem',
                pointerEvents: 'none'
              }}
            >
              Scrolling...
            </Box>
          )}
        </SmoothScrollContainer>
      )}
    </StyledContainer>
  );
};

export default SmoothScrollList;